import React, { forwardRef } from 'react';
import { cn } from './cn';

const VARIANTS = {
  primary:
    'bg-brand-600 text-white border border-brand-600 hover:bg-brand-500 hover:border-brand-500 active:bg-brand-700 disabled:bg-brand-600/50 disabled:border-brand-600/50',
  secondary:
    'bg-surface text-fg border border-line hover:border-line-strong hover:bg-ink-50 active:bg-ink-100 dark:hover:bg-ink-800 dark:active:bg-ink-700 disabled:bg-surface dark:disabled:hover:bg-surface disabled:text-fg-subtle',
  ghost:
    'bg-transparent text-fg-muted border border-transparent hover:bg-ink-100 hover:text-fg active:bg-ink-200 dark:hover:bg-ink-700 dark:active:bg-ink-600 disabled:text-fg-subtle',
  danger:
    'bg-danger-600 text-white border border-danger-600 hover:bg-danger-500 hover:border-danger-500 active:bg-danger-700 disabled:bg-danger-600/50 disabled:border-danger-600/50',
  'danger-ghost':
    'bg-transparent text-danger-600 border border-transparent hover:bg-danger-50 active:bg-danger-100 dark:text-danger-500 dark:hover:bg-danger-500/10 dark:active:bg-danger-500/20 disabled:text-danger-600/50',
  link:
    'bg-transparent text-brand-600 dark:text-brand-400 border border-transparent hover:underline underline-offset-2 p-0 h-auto',
};

const SIZES = {
  sm: 'h-7 px-2.5 text-caption gap-1.5 rounded-md',
  md: 'h-8 px-3 text-body-sm gap-2 rounded-md',
  lg: 'h-10 px-4 text-body gap-2 rounded-lg',
};

const Spinner = ({ className }) => (
  <svg
    className={cn('animate-spin', className)}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path
      d="M21 12a9 9 0 0 0-9-9"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Button — primary interactive primitive.
 *   variant: primary | secondary | ghost | danger | danger-ghost | link
 *   size:    sm | md (default) | lg
 *   loading: swaps leading icon for a spinner, disables the button
 *   iconLeft / iconRight: icon elements
 *   fullWidth
 */
export const Button = forwardRef(function Button(
  {
    as: Comp = 'button',
    variant = 'primary',
    size = 'md',
    loading = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    disabled,
    className,
    children,
    type,
    ...rest
  },
  ref,
) {
  const isButton = Comp === 'button';
  return (
    <Comp
      ref={ref}
      type={isButton ? type || 'button' : undefined}
      disabled={isButton ? disabled || loading : undefined}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium whitespace-nowrap select-none',
        'transition-[background-color,border-color,color,box-shadow] duration-150 ease-out-soft',
        'focus-visible:outline-none focus-visible:shadow-focus-ring',
        'disabled:cursor-not-allowed',
        SIZES[size],
        VARIANTS[variant],
        fullWidth && 'w-full',
        variant === 'danger' && 'focus-visible:shadow-focus-ring-danger',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner />
      ) : iconLeft ? (
        <span className="inline-flex shrink-0 items-center">{iconLeft}</span>
      ) : null}
      {children}
      {iconRight && !loading ? (
        <span className="inline-flex shrink-0 items-center">{iconRight}</span>
      ) : null}
    </Comp>
  );
});

export default Button;
